import { useEffect, useState, CSSProperties } from 'react';
import { IconFileTypePdf, IconCheck, IconAlertTriangle, IconX, IconRefresh } from '@tabler/icons-react';
import { V4 } from '../../../tokens';
import { DotsLoader } from '../../../DotsLoader/DotsLoader';
import type { UploadQueueItem } from '../types';

export interface QueueItemCardProps {
  item: UploadQueueItem;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}

const MAX_NAME = 14;

function truncate(name: string): string {
  if (name.length <= MAX_NAME) return name;
  return `${name.slice(0, MAX_NAME)}…`;
}

function StatusBadge({ item }: { item: UploadQueueItem }) {
  const badgeBaseStyle: CSSProperties = {
    position: 'absolute',
    right: 4,
    bottom: 4,
    width: 22,
    height: 22,
    borderRadius: 11,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1.5px solid ${V4.white}`,
  };

  switch (item.status) {
    case 'queued':
      return (
        <span
          data-testid={`queue-item-${item.id}-status-queued`}
          aria-label="En cola"
          style={{ ...badgeBaseStyle, background: V4.grayUltraDark }}
        >
          <span style={{ width: 6, height: 6, borderRadius: 3, background: V4.white }} />
        </span>
      );
    case 'uploading':
      return (
        <span
          data-testid={`queue-item-${item.id}-status-uploading`}
          aria-label="Subiendo"
          style={{ ...badgeBaseStyle, background: V4.secondary }}
        >
          <DotsLoader size={4} color={V4.white} />
        </span>
      );
    // 'analyzing' NO lleva badge per-file: el backend procesa todo el batch en
    // UNA llamada, así que un loader por archivo sugeriría (falsamente) análisis
    // foto-por-foto. La actividad la comunica ProcessingProgress a nivel batch.
    case 'analyzing':
      return null;
    case 'completed':
      return (
        <span
          data-testid={`queue-item-${item.id}-status-completed`}
          aria-label="Completado"
          style={{ ...badgeBaseStyle, background: V4.accentMint }}
        >
          <IconCheck size={14} color={V4.white} stroke={3} aria-hidden="true" />
        </span>
      );
    case 'failed':
      return (
        <span
          data-testid={`queue-item-${item.id}-status-failed`}
          aria-label="Falló"
          style={{ ...badgeBaseStyle, background: V4.danger }}
        >
          <IconAlertTriangle size={14} color={V4.white} stroke={2.5} aria-hidden="true" />
        </span>
      );
    case 'cancelled':
      return (
        <span
          data-testid={`queue-item-${item.id}-status-cancelled`}
          aria-label="Cancelado"
          style={{ ...badgeBaseStyle, background: V4.grayDark }}
        >
          <IconX size={14} color={V4.white} stroke={2.5} aria-hidden="true" />
        </span>
      );
    default:
      return null;
  }
}

function Thumbnail({ item }: { item: UploadQueueItem }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    // Items reconstruidos tras un reload traen `previewUrl` (download URL de
    // Storage) y un File stub vacío: no generar objectURL en ese caso.
    if (item.kind !== 'image' || item.previewUrl) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(item.file);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [item.file, item.kind, item.previewUrl]);

  const previewUrl = item.previewUrl ?? objectUrl;

  if (item.kind === 'image' && previewUrl) {
    return (
      <img
        src={previewUrl}
        alt={item.file.name}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: V4.grayUltraLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IconFileTypePdf size={40} color={V4.dangerAlt} stroke={1.5} aria-hidden="true" />
    </div>
  );
}

export function QueueItemCard({ item, onRemove, onRetry }: QueueItemCardProps) {
  const [removeHover, setRemoveHover] = useState(false);
  const [retryHover, setRetryHover] = useState(false);

  const inProgress = item.status === 'uploading' || item.status === 'analyzing';
  const showRemove = !inProgress;
  const showRetry = item.status === 'failed';

  const cardStyle: CSSProperties = {
    width: '100%',
    minHeight: 96,
    borderRadius: 8,
    border: `1px solid ${V4.grayLight}`,
    background: V4.white,
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    boxSizing: 'border-box',
  };

  return (
    <div className="sm-queue-item-animate" data-testid={`queue-item-${item.id}`} style={cardStyle}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          borderRadius: 6,
          overflow: 'hidden',
          background: V4.grayUltraLight,
        }}
      >
        <Thumbnail item={item} />
        <StatusBadge item={item} />

        {showRemove && (
          <button
            type="button"
            data-testid={`queue-item-${item.id}-remove`}
            onClick={() => onRemove(item.id)}
            onMouseEnter={() => setRemoveHover(true)}
            onMouseLeave={() => setRemoveHover(false)}
            aria-label={`Quitar ${item.file.name}`}
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 22,
              height: 22,
              borderRadius: 11,
              border: 'none',
              background: removeHover ? V4.danger : 'rgba(34, 36, 36, 0.65)',
              color: V4.white,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              transition: 'background 150ms ease',
            }}
          >
            <IconX size={14} stroke={2.5} aria-hidden="true" />
          </button>
        )}
      </div>

      <div
        title={item.file.name}
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: V4.primary,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {truncate(item.file.name)}
      </div>

      {showRetry && (
        <button
          type="button"
          data-testid={`queue-item-${item.id}-retry`}
          onClick={() => onRetry(item.id)}
          onMouseEnter={() => setRetryHover(true)}
          onMouseLeave={() => setRetryHover(false)}
          style={{
            background: retryHover ? V4.grayUltraLight : V4.white,
            color: V4.danger,
            border: `1px solid ${V4.danger}`,
            borderRadius: 4,
            padding: '4px 6px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            transition: 'background 150ms ease',
          }}
        >
          <IconRefresh size={12} stroke={2.5} aria-hidden="true" />
          Reintentar
        </button>
      )}
    </div>
  );
}
